import { useCallback, useMemo, useState, ReactNode, useEffect } from "react";
import { AuthContext } from "./authProvider";
import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "@posthog/react";

const TOKEN_STORAGE_KEY = "token";
const AUTH_METHOD_KEY = "hockeyly_auth_method";

interface TokenAuthProviderProps {
    children: ReactNode;
}

const TokenAuthProvider = ({ children }: TokenAuthProviderProps) => {
    const [token, setTokenState] = useState<string | null>(localStorage.getItem(TOKEN_STORAGE_KEY));
    const queryClient = useQueryClient();
    const posthog = usePostHog();

    const setToken = useCallback((newToken: string | null) => {
        setTokenState(newToken);
        if (newToken) {
            localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        } else {
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
    }, []);

    const logout = useCallback(() => {
        posthog.reset();
        setTokenState(null);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(AUTH_METHOD_KEY);
        queryClient.clear();
        window.location.href = "/login";
    }, [posthog, queryClient]);

    const login = useCallback(async () => {}, []);

    useEffect(() => {
        if (token) {
            queryClient.invalidateQueries({ queryKey: ["featureFlags"] });
        }
    }, [token, queryClient]);

    const contextValue = useMemo(
        () => ({
            token,
            setToken,
            logout,
            login,
            isAuthenticated: !!token,
            isLoading: false,
            selectedTenant: null,
            authMethod: "token" as const,
        }),
        [token, setToken, logout, login]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default TokenAuthProvider;
